import type { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import type { Actions } from '../Action';
import { ActionMenuItem } from './ActionMenuItem';

interface ActionDropdownProps {
  readonly actions: Actions;
  /** Action names to offer, in render order. Names absent from `actions` are skipped. */
  readonly items: readonly string[];
  /** Override the "⋯" toggle content. */
  readonly toggle?: ReactNode;
  /** Disable the whole menu — a row-level lock (e.g. a record mid-deletion) that overrides per-action state. */
  readonly disabled?: boolean;
  /** Which edge of the toggle the menu lines up with; 'end' for a toggle near the right edge. */
  readonly align?: 'start' | 'end';
}

/**
 * A "⋯" dropdown of the given named actions, in the order provided — the caller
 * owns ordering and which actions appear. Items absent from `actions` are
 * skipped; if none remain, the dropdown renders nothing.
 */
export const ActionDropdown = ({ actions, items, toggle, disabled, align = 'start' }: ActionDropdownProps) => {
  const present = items.filter((name) => actions[name]);
  if (present.length === 0) return null;
  return (
    <Dropdown>
      <Dropdown.Toggle variant="phoenix-secondary" size="sm" className="no-caret" disabled={disabled}>
        {toggle ?? <FontAwesomeIcon icon={faEllipsis} className="fs-10" />}
      </Dropdown.Toggle>
      <Dropdown.Menu align={align}>
        {present.map((name) => (
          <ActionMenuItem key={name} actions={actions} action={name} />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
